import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Coins, CheckCircle, Users, DollarSign, ArrowRight, Clock, Shield, Download } from "lucide-react";
import { useEffect } from "react";
import { trackEvent } from "@/utils/analytics";
import { InstallAppButton } from "@/components/InstallAppButton";

const Index = () => {
  useEffect(() => {
    trackEvent("homepage_visit");
  }, []);
  return <div className="min-h-screen">
      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-background via-secondary/30 to-background">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMDAiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItaDJ2LTJoLTJ6bTAtNGgydi0yaC0ydjJ6bTAgOGgydjJoLTJ2LTJ6bTItMnYyaDJ2LTJoLTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50"></div>
        
        <div className="container relative mx-auto px-4 py-20 lg:py-32">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium">
                <Coins className="h-4 w-4" />
                Start Earning Today
              </div>
              <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                Join Vividad and start{" "}
                <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                  earning by completing tasks
                </span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl">
                Complete simple tasks, earn points, and get paid through Payoneer, Visa Card, or Mobile Money. It's that easy.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/auth">
                  <Button size="lg" className="text-lg px-8">
                    Get Started Free
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <InstallAppButton variant="hero" />
              </div>
              <p className="text-sm text-muted-foreground">
                Get started. No credit card required. 100% free to join.
              </p>
            </div>
            <div className="flex-1 flex items-center justify-center">
              
            </div>
          </div>
        </div>
      </section>


      {/* Features Section */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-4">
              Why Choose VIVIDAD?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Simple, secure, and reliable platform for earning money online
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            <Card className="p-8 text-center hover:shadow-[var(--shadow-card)] transition-all duration-300 hover:-translate-y-1 border-border/50">
              <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-accent" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Easy Tasks</h3>
              <p className="text-muted-foreground leading-relaxed">
                Complete surveys, watch videos, and try apps to earn points effortlessly.
              </p>
            </Card>
            <Card className="p-8 text-center hover:shadow-[var(--shadow-card)] transition-all duration-300 hover:-translate-y-1 border-border/50">
              <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <DollarSign className="h-8 w-8 text-accent" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Fast Payouts</h3>
              <p className="text-muted-foreground leading-relaxed">
                Request a payout via Payoneer, Visa Card, or Mobile Money. Every 100 points equals $1.
              </p>
            </Card>
            <Card className="p-8 text-center hover:shadow-[var(--shadow-card)] transition-all duration-300 hover:-translate-y-1 border-border/50">
              <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-accent" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Refer & Earn</h3>
              <p className="text-muted-foreground leading-relaxed">
                Invite friends and earn bonus rewards when they complete their first task.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 lg:py-32 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-4">
              How to Start Earning
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Three simple steps to start making money today
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {[{
              step: "1",
              title: "Sign Up Free",
              description: "Create your account in under a minute. No credit card needed."
            }, {
              step: "2",
              title: "Complete Tasks",
              description: "Pick from available tasks and start earning points right away."
            }, {
              step: "3",
              title: "Get Paid",
              description: "Cash out once you hit the minimum balance. Payments processed within 3-5 days."
            }].map(item => <Card key={item.step} className="p-8 text-center hover:shadow-[var(--shadow-card)] transition-all duration-300 border-border/50">
                <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent text-white text-2xl font-bold flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary to-accent opacity-50 blur-md"></div>
                  <span className="relative">{item.step}</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{item.description}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Platform Guidelines Section */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-4">
              Important Platform Guidelines
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Please read and understand how our platform works before signing up
            </p>
          </div>
          
          <div className="grid gap-8 md:grid-cols-2 max-w-5xl mx-auto">
            {/* Multi-Step Verification */}
            <Card className="p-8 border-primary/30 bg-primary/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">Multi-Step Verification</h3>
              </div>
              <p className="text-muted-foreground mb-4">
                This platform operates through a <strong>legitimate affiliate approval system</strong>. 
                Payments do not occur immediately after completing one task.
              </p>
              <div className="bg-background/50 rounded-lg p-4 space-y-2">
                <p className="font-medium text-sm">Each task must be approved by:</p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2">
                  <li>The advertiser who created the offer</li>
                  <li>The affiliate network managing the campaign</li>
                  <li>Our platform for final verification</li>
                </ol>
                <p className="text-sm text-muted-foreground mt-2">
                  This process typically takes <strong>about one week</strong>.
                </p>
              </div>
            </Card>

            {/* Security Standards */}
            <Card className="p-8 border-destructive/30 bg-destructive/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-destructive" />
                </div>
                <h3 className="text-xl font-semibold">Strict Security Standards</h3>
              </div>
              <p className="text-muted-foreground mb-4">
                To protect the platform and ensure fair earnings for all users, we enforce strict security policies.
              </p>
              <div className="bg-background/50 rounded-lg p-4">
                <p className="font-medium text-sm mb-2 text-destructive">Automatic Disqualification:</p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-destructive font-bold">×</span>
                    Using VPNs or proxy services
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-destructive font-bold">×</span>
                    Using bots or automated tools
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-destructive font-bold">×</span>
                    Providing fake information
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-destructive font-bold">×</span>
                    Bypassing verification systems
                  </li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <Card className="p-12 text-center bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 border-accent/20 shadow-[var(--shadow-card)]">
            <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
              <Coins className="h-10 w-10 text-accent" />
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
              Join Vividad and start earning today. It's free, easy, and available to everyone.
            </p>
            <Link to="/auth">
              <Button size="lg" className="text-lg px-12 shadow-lg hover:shadow-xl transition-all duration-300">
                Join VIVIDAD Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </Card>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-12 bg-card/50">
        <div className="container mx-auto px-4">
          <div className="grid gap-8 md:grid-cols-4">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Coins className="h-6 w-6 text-accent" />
                <span className="text-xl font-bold">VIVIDAD</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Your trusted platform for earning money online
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Platform</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/tasks" className="hover:text-accent">Browse Tasks</Link></li>
                <li><Link to="/referrals" className="hover:text-accent">Referrals</Link></li>
                <li><Link to="/payouts" className="hover:text-accent">Payouts</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/about" className="hover:text-accent">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-accent">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/legal" className="hover:text-accent">Legal & Policy</Link></li>
                <li><Link to="/legal" className="hover:text-accent">Privacy Policy</Link></li>
                <li><Link to="/legal" className="hover:text-accent">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t mt-12 pt-8 text-center text-sm text-muted-foreground">
            <p>&copy; 2025 VIVIDAD. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>;
};
export default Index;